import { clamp, createSeededRandom } from "./utils.js";

const TAU = Math.PI * 2;

export const PARTICLE_MODELS = Object.freeze([
  { id: "heart", label: "Heart", primary: "#ff5f82", secondary: "#ffd0dc" },
  { id: "flower", label: "Flower", primary: "#6edcff", secondary: "#b08cff" },
  { id: "saturn", label: "Saturn", primary: "#79dfff", secondary: "#d7b56d" },
  { id: "firework", label: "Firework", primary: "#72e9ff", secondary: "#ff78c8" },
  { id: "earth", label: "Earth", primary: "#328dff", secondary: "#8de3a8" },
  { id: "landscape", label: "千里江山", primary: "#55d6c2", secondary: "#d7b56d" },
  { id: "epiphyllum", label: "昙花", primary: "#bca8ff", secondary: "#f3ddff" },
]);

const MODEL_MAP = new Map(PARTICLE_MODELS.map((model) => [model.id, model]));

/**
 * Generates deterministic particle buffers for one of the seven art models.
 */
export function generateParticleModel(name, count, seed = 0x7f4a7c15) {
  const definition = MODEL_MAP.get(name);
  if (!definition) {
    throw new RangeError("Unknown particle model: " + name);
  }
  if (!Number.isInteger(count) || count <= 0) {
    throw new RangeError("Particle count must be a positive integer.");
  }

  const buffers = createBuffers(count);
  const random = createSeededRandom(seed ^ hashString(name));
  const gaussian = createGaussian(random);

  const generators = {
    heart: generateHeart,
    flower: generateFlower,
    saturn: generateSaturn,
    firework: generateFirework,
    earth: generateEarth,
    landscape: generateLandscape,
    epiphyllum: generateEpiphyllum,
  };

  generators[name](buffers, random, gaussian);

  return {
    name,
    positions: buffers.positions,
    sizeWeights: buffers.sizeWeights,
    colorWeights: buffers.colorWeights,
    bounds: {
      center: [0, 0, 0],
      radius: measureRadius(buffers.positions),
    },
    palette: {
      primary: definition.primary,
      secondary: definition.secondary,
    },
  };
}

export function isParticleModelName(name) {
  return MODEL_MAP.has(name);
}

function createBuffers(count) {
  return {
    count,
    positions: new Float32Array(count * 3),
    sizeWeights: new Float32Array(count),
    colorWeights: new Float32Array(count),
  };
}

function writeParticle(buffers, index, x, y, z, size, colorWeight) {
  const offset = index * 3;
  buffers.positions[offset] = x;
  buffers.positions[offset + 1] = y;
  buffers.positions[offset + 2] = z;
  buffers.sizeWeights[index] = clamp(size, 0.35, 3.5);
  buffers.colorWeights[index] = clamp(colorWeight, 0, 1);
}

function createGaussian(random) {
  return () => {
    const u = Math.max(random(), Number.EPSILON);
    const v = random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(TAU * v);
  };
}

function generateHeart(buffers, random, gaussian) {
  for (let index = 0; index < buffers.count; index += 1) {
    const angle = random() * TAU;
    const outlineX = 16 * Math.pow(Math.sin(angle), 3);
    const outlineY =
      13 * Math.cos(angle) -
      5 * Math.cos(2 * angle) -
      2 * Math.cos(3 * angle) -
      Math.cos(4 * angle);
    const shellBias = random() < 0.7;
    const fill = shellBias ? 0.8 + random() * 0.2 : Math.sqrt(random()) * 0.82;
    const x = outlineX * 0.112 * fill + gaussian() * 0.018;
    const y = outlineY * 0.105 * fill + 0.28 + gaussian() * 0.018;
    const thickness = 0.12 + (1 - fill * 0.52) * 0.38;
    const z = gaussian() * 0.055 + (random() - 0.5) * thickness;
    const size = 0.48 + Math.pow(random(), 5) * 1.75;
    const color = 0.22 + (y + 1.55) / 3.35 * 0.48 + Math.abs(z) * 0.28;
    writeParticle(buffers, index, x, y, z, size, color);
  }
}

function generateFlower(buffers, random, gaussian) {
  const petalCounts = [8, 11, 15];
  const petalLengths = [2.15, 1.72, 1.34];
  const petalWidths = [0.43, 0.33, 0.24];

  for (let index = 0; index < buffers.count; index += 1) {
    if (random() < 0.12) {
      const radius = Math.sqrt(random()) * 0.48;
      const angle = random() * TAU;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      const z = 0.34 - radius * 0.34 + gaussian() * 0.045;
      writeParticle(
        buffers,
        index,
        x,
        y,
        z,
        0.58 + Math.pow(random(), 5) * 1.35,
        random() * 0.22,
      );
      continue;
    }

    const layer = Math.floor(random() * 3);
    const petalCount = petalCounts[layer];
    const petal = Math.floor(random() * petalCount);
    const phase = layer * 0.19;
    const angle = (petal / petalCount) * TAU + phase;
    const u = Math.pow(random(), 0.68);
    const radius = 0.24 + petalLengths[layer] * u;
    const width =
      Math.sin(Math.PI * u) *
      petalWidths[layer] *
      (random() - 0.5) *
      2;
    const tangentX = -Math.sin(angle);
    const tangentY = Math.cos(angle);
    const x = Math.cos(angle) * radius + tangentX * width;
    const y = Math.sin(angle) * radius + tangentY * width;
    const z =
      Math.sin(Math.PI * u) * (0.38 - layer * 0.055) -
      u * (0.22 + layer * 0.055) +
      gaussian() * 0.026;
    writeParticle(
      buffers,
      index,
      x,
      y,
      z,
      0.45 + Math.pow(random(), 5.6) * 1.8,
      0.28 + u * 0.72,
    );
  }
}

function generateSaturn(buffers, random, gaussian) {
  const ringTilt = 1.28;
  const cosTilt = Math.cos(ringTilt);
  const sinTilt = Math.sin(ringTilt);

  for (let index = 0; index < buffers.count; index += 1) {
    if (random() < 0.49) {
      const normalizedY = random() * 2 - 1;
      const radiusAtY = Math.sqrt(Math.max(0, 1 - normalizedY * normalizedY));
      const angle = random() * TAU;
      const radius = 1.08 + gaussian() * 0.025;
      const x = Math.cos(angle) * radiusAtY * radius;
      const y = normalizedY * radius;
      const z = Math.sin(angle) * radiusAtY * radius;
      const bands = Math.sin(normalizedY * 23) * 0.13;
      writeParticle(
        buffers,
        index,
        x,
        y,
        z,
        0.55 + Math.pow(random(), 5.5) * 1.5,
        0.36 + normalizedY * 0.18 + bands + random() * 0.12,
      );
      continue;
    }

    let radius = 1.43 + Math.pow(random(), 0.78) * 1.46;
    if (radius > 1.91 && radius < 2.1) {
      radius += 0.2;
    }
    const angle = random() * TAU;
    const planeX = Math.cos(angle) * radius;
    const planeY = Math.sin(angle) * radius;
    const planeZ = gaussian() * (0.015 + radius * 0.006);
    const y = planeY * cosTilt - planeZ * sinTilt;
    const z = planeY * sinTilt + planeZ * cosTilt;
    const band = Math.sin(radius * 31) * 0.16;
    writeParticle(
      buffers,
      index,
      planeX,
      y,
      z,
      0.38 + Math.pow(random(), 6.2) * 1.55,
      0.58 + band + random() * 0.2,
    );
  }
}

function generateFirework(buffers, random, gaussian) {
  const centers = [
    [-1.55, 0.72, 0.05, 0.92],
    [0.05, 1.02, -0.12, 1.02],
    [1.55, 0.5, 0.12, 0.82],
    [-0.78, -0.72, 0.16, 0.74],
    [0.98, -0.78, -0.05, 0.78],
  ];
  const raysPerBurst = 44;
  const rays = centers.map(() =>
    Array.from({ length: raysPerBurst }, () => {
      const angle = random() * TAU;
      const elevation = (random() - 0.5) * 1.1;
      return [
        Math.cos(angle) * Math.cos(elevation),
        Math.sin(angle) * Math.cos(elevation),
        Math.sin(elevation) * 0.45,
      ];
    }),
  );

  for (let index = 0; index < buffers.count; index += 1) {
    const burstIndex = Math.floor(random() * centers.length);
    const center = centers[burstIndex];
    const ray = rays[burstIndex][Math.floor(random() * raysPerBurst)];
    const distance = Math.pow(random(), 0.46) * 1.28 * center[3];
    const trailNoise = 0.012 + distance * 0.009;
    const x = center[0] + ray[0] * distance + gaussian() * trailNoise;
    const y =
      center[1] +
      ray[1] * distance -
      distance * distance * 0.16 +
      gaussian() * trailNoise;
    const z = center[2] + ray[2] * distance + gaussian() * trailNoise;
    writeParticle(
      buffers,
      index,
      x,
      y,
      z,
      0.42 + Math.pow(random(), 4.8) * 2.15,
      clamp(burstIndex / (centers.length - 1) * 0.66 + distance * 0.22, 0, 1),
    );
  }
}

function generateEarth(buffers, random, gaussian) {
  for (let index = 0; index < buffers.count; index += 1) {
    const normalizedY = random() * 2 - 1;
    const radiusAtY = Math.sqrt(Math.max(0, 1 - normalizedY * normalizedY));
    const angle = random() * TAU;
    const directionX = Math.cos(angle) * radiusAtY;
    const directionY = normalizedY;
    const directionZ = Math.sin(angle) * radiusAtY;
    const longitude = Math.atan2(directionX, directionZ) * 180 / Math.PI;
    const latitude = Math.asin(directionY) * 180 / Math.PI;
    const land = continentField(longitude, latitude);
    const isCloud = random() < 0.075;
    const radius = 1.72 + (land > 0.36 ? 0.035 : 0) + (isCloud ? 0.075 : 0);
    const jitter = gaussian() * 0.006;
    const color = isCloud ? 0.58 : land > 0.36 ? 0.9 : 0.08 + random() * 0.18;
    const size = isCloud
      ? 0.42 + Math.pow(random(), 5) * 1.3
      : land > 0.36
        ? 0.62 + Math.pow(random(), 5) * 1.55
        : 0.43 + Math.pow(random(), 6) * 1.15;
    writeParticle(
      buffers,
      index,
      directionX * (radius + jitter),
      directionY * (radius + jitter),
      directionZ * (radius + jitter),
      size,
      color,
    );
  }
}

function continentField(longitude, latitude) {
  const blobs = [
    [18, 7, 22, 32, 1],
    [18, 49, 27, 16, 0.92],
    [64, 47, 48, 23, 0.9],
    [44, 26, 19, 13, 0.85],
    [-104, 47, 38, 27, 0.92],
    [-72, 18, 19, 19, 0.72],
    [-59, -19, 18, 37, 0.96],
    [135, -25, 20, 15, 0.9],
    [-42, 73, 15, 12, 0.8],
  ];

  let strength = latitude < -70 ? 0.52 + (-latitude - 70) * 0.02 : 0;

  for (const [centerX, centerY, radiusX, radiusY, weight] of blobs) {
    let deltaLongitude = Math.abs(longitude - centerX);
    deltaLongitude = Math.min(deltaLongitude, 360 - deltaLongitude);
    const dx = deltaLongitude / radiusX;
    const dy = (latitude - centerY) / radiusY;
    strength = Math.max(strength, Math.exp(-(dx * dx + dy * dy) * 1.55) * weight);
  }

  const coastline =
    Math.sin(longitude * 0.19 + latitude * 0.13) * 0.055 +
    Math.sin(longitude * 0.07 - latitude * 0.31) * 0.045;
  return strength + coastline;
}

function generateLandscape(buffers, random, gaussian) {
  const layerPhases = [0.4, 1.8, 3.05, 4.6];

  for (let index = 0; index < buffers.count; index += 1) {
    const component = random();
    if (component > 0.92) {
      const x = random() * 6.5 - 3.25;
      const y = -1.32 + Math.sin(x * 1.35) * 0.085 + gaussian() * 0.025;
      const z = 1.0 + gaussian() * 0.035;
      writeParticle(
        buffers,
        index,
        x,
        y,
        z,
        0.4 + Math.pow(random(), 5) * 1.35,
        0.12 + random() * 0.18,
      );
      continue;
    }

    if (component > 0.78) {
      const band = Math.floor(random() * 5);
      const centers = [-2.4, -1.15, 0.15, 1.35, 2.55];
      const angle = random() * TAU;
      const width = 0.42 + random() * 0.48;
      const x = centers[band] + Math.cos(angle) * width + gaussian() * 0.06;
      const y =
        0.2 +
        band % 2 * 0.42 +
        Math.sin(angle * 2) * 0.055 +
        gaussian() * 0.045;
      const z = 0.55 - band * 0.16 + gaussian() * 0.06;
      writeParticle(
        buffers,
        index,
        x,
        y,
        z,
        0.38 + Math.pow(random(), 6) * 1.25,
        0.22 + random() * 0.2,
      );
      continue;
    }

    const layer = Math.floor(random() * 4);
    const x = random() * 6.6 - 3.3;
    const phase = layerPhases[layer];
    const ridge =
      0.38 +
      Math.pow(Math.abs(Math.sin(x * (0.82 + layer * 0.11) + phase)), 1.65) * 0.7 +
      Math.pow(Math.abs(Math.sin(x * 0.43 - phase * 0.72)), 5) * 0.72;
    const baseY = -1.36 + layer * 0.14;
    const surfaceBias = random() < 0.62;
    const fill = surfaceBias ? 0.82 + random() * 0.18 : Math.pow(random(), 0.62);
    const y = baseY + ridge * fill + gaussian() * 0.018;
    const z = -0.82 + layer * 0.47 + gaussian() * 0.055;
    writeParticle(
      buffers,
      index,
      x + gaussian() * 0.018,
      y,
      z,
      0.4 + Math.pow(random(), 5.5) * 1.65,
      0.3 + layer * 0.16 + fill * 0.16,
    );
  }
}

function generateEpiphyllum(buffers, random, gaussian) {
  const petalCounts = [18, 15, 12];

  for (let index = 0; index < buffers.count; index += 1) {
    if (random() < 0.1) {
      const angle = random() * TAU;
      const length = 0.16 + Math.pow(random(), 0.62) * 1.18;
      const x = Math.cos(angle) * length;
      const y = Math.sin(angle) * length * 0.86;
      const z = 0.38 + length * 0.18 + gaussian() * 0.035;
      writeParticle(
        buffers,
        index,
        x,
        y,
        z,
        0.48 + Math.pow(random(), 5.2) * 1.35,
        random() * 0.2,
      );
      continue;
    }

    const layer = Math.floor(random() * 3);
    const petalCount = petalCounts[layer];
    const petal = Math.floor(random() * petalCount);
    const angle = (petal / petalCount) * TAU + layer * 0.11;
    const u = Math.pow(random(), 0.76);
    const curl = clamp((u - 0.73) / 0.27, 0, 1);
    const radius = 0.17 + u * (2.42 - layer * 0.16) - curl * curl * 0.34;
    const width =
      Math.sin(Math.PI * u) *
      (0.16 - layer * 0.018) *
      (random() - 0.5) *
      2;
    const tangentX = -Math.sin(angle);
    const tangentY = Math.cos(angle);
    const x = Math.cos(angle) * radius + tangentX * width;
    const y = Math.sin(angle) * radius * 0.88 + tangentY * width;
    const z =
      0.12 +
      Math.sin(Math.PI * u) * 0.42 +
      curl * curl * 0.55 -
      layer * 0.055 +
      gaussian() * 0.018;
    writeParticle(
      buffers,
      index,
      x,
      y,
      z,
      0.42 + Math.pow(random(), 5.8) * 1.75,
      0.34 + u * 0.66,
    );
  }
}

function measureRadius(positions) {
  let maximumSquared = 0;
  for (let index = 0; index < positions.length; index += 3) {
    const x = positions[index];
    const y = positions[index + 1];
    const z = positions[index + 2];
    maximumSquared = Math.max(maximumSquared, x * x + y * y + z * z);
  }
  return Math.sqrt(maximumSquared);
}

function hashString(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
